/**
 * Conversation history (the only way to switch chats at phone width).
 *
 * Built on the shared Sheet (de-slop CHAT-14): the old page-sheet wrapped a
 * flex:1 ScrollView in an auto-height `maxHeight: 100vh` box, so on mobile
 * Safari the end of the list and its rename/delete actions sat under the
 * browser toolbar. The Sheet caps height with `dvh` and scrolls its body.
 */
import React from 'react';
import { TouchableOpacity, View } from 'react-native';
import { Swipeable } from 'react-native-gesture-handler';
import { MaterialIcons } from '@expo/vector-icons';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Sheet } from '@/components/ui/Sheet';
import { Text } from '@/components/ui/text';
import { useTranslation } from '../../../hooks/useTranslation';
import type { ChatSession } from '../../../types';
import { COLORS } from '../chatConstants';
import { formatDateTime } from '../chatFormat';

export interface SessionsSheetProps {
  visible: boolean;
  onClose: () => void;
  sessions: ChatSession[];
  currentSessionId: number | null;
  onNewChat: () => void;
  onSelect: (sessionId: number) => void;
  onRename: (sessionId: number, currentTitle: string) => void;
  onDelete: (sessionId: number) => void;
}

export function SessionsSheet({
  visible,
  onClose,
  sessions,
  currentSessionId,
  onNewChat,
  onSelect,
  onRename,
  onDelete,
}: SessionsSheetProps) {
  const { t } = useTranslation();

  return (
    <Sheet
      visible={visible}
      onClose={onClose}
      title={t('chat.chatSessions')}
      closeLabel={t('common.close')}
      testID="chat_sessions_sheet"
      maxWidth={640}
    >
      <View style={{ alignItems: 'flex-end', marginBottom: 12 }}>
        <Button variant="default" size="sm" onPress={onNewChat}>
          <Text className="text-primary-foreground font-semibold">{t('chat.newChat')}</Text>
        </Button>
      </View>

      {sessions.length === 0 ? (
        <View className="justify-center items-center py-12">
          <MaterialIcons name="chat" size={64} color="#cbd5e1" />
          <Text className="text-lg font-bold text-muted-foreground mt-4 text-center">
            {t('chat.noConversations')}
          </Text>
          <Text className="text-sm text-muted-foreground mt-2 text-center">
            {t('chat.startFirstChat')}
          </Text>
        </View>
      ) : (
        <View className="gap-2">
          {sessions.map((item) => {
            const isCurrent = currentSessionId === item.id;
            const title = item.title || `${t('chat.session')} ${item.id}`;
            return (
              <Swipeable
                key={item.id}
                renderRightActions={() => (
                  <TouchableOpacity
                    onPress={() => onDelete(item.id)}
                    style={{
                      width: 88,
                      backgroundColor: '#ef4444',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginVertical: 6,
                      borderRadius: 8,
                    }}
                    accessibilityLabel={t('chat.deleteConversation')}
                    accessibilityRole="button"
                  >
                    <MaterialIcons name="delete" size={22} color="white" />
                  </TouchableOpacity>
                )}
              >
                <Card
                  className={isCurrent ? 'border-2 bg-blue-50' : 'bg-white border-slate-200'}
                  style={{ borderColor: isCurrent ? COLORS.primary : COLORS.border }}
                >
                  <CardContent className="p-4">
                    <View className="flex-row items-center">
                      <TouchableOpacity
                        className="flex-row items-center flex-1"
                        onPress={() => onSelect(item.id)}
                        testID={`chat_session_row_${item.id}`}
                        accessibilityRole="button"
                      >
                        <MaterialIcons
                          name="chat-bubble"
                          size={20}
                          color={isCurrent ? COLORS.primary : COLORS.text.secondary}
                        />
                        <View className="flex-1 ml-3">
                          <Text className={`text-base font-semibold mb-1 ${isCurrent ? 'text-blue-600' : 'text-foreground'}`}>
                            {title}
                          </Text>
                          <Text className="text-xs text-muted-foreground">
                            {formatDateTime(item.created_at)}
                          </Text>
                        </View>
                        {isCurrent && (
                          <MaterialIcons name="check" size={20} color={COLORS.primary} />
                        )}
                      </TouchableOpacity>

                      <TouchableOpacity
                        onPress={() => onRename(item.id, title)}
                        style={{ padding: 8, marginLeft: 8 }}
                        accessibilityLabel={t('chat.renameSession')}
                        accessibilityRole="button"
                      >
                        <MaterialIcons name="edit" size={18} color={COLORS.text.secondary} />
                      </TouchableOpacity>
                    </View>
                  </CardContent>
                </Card>
              </Swipeable>
            );
          })}
        </View>
      )}
    </Sheet>
  );
}
